import type { AudioAssetRef, Project } from '@/domain/types';
import { clipEndTicks } from '@/domain/selectors';
import { trackChannelCount } from '@/domain/freeze';
import { runCommand } from '@/commands/registry';
import type { AssetManager } from '@/assets/AssetManager';
import { encodeWav } from '@/util/wav';
import { createId } from '@/util/id';
import { bufferToChannels, renderProject } from './render';

/**
 * Consolidating: the clips inside a range, rendered down to one clip each
 * track, on the same track.
 *
 * A phrase built from six clips — trimmed, crossfaded, one stretched, one with
 * a ride drawn on it — is six things to keep in step every time it is moved.
 * Consolidating renders those clips, and *only* the clips, and puts one clip
 * of the render where they were. The inserts, fader and automation are not in
 * the render because they stay live on the track and would otherwise be
 * applied a second time; this is the difference from a bounce, which bakes
 * them in and therefore has to land on a new track.
 *
 * The render is the same one freezing uses, tapped one stage earlier: the
 * track alone, before its inserts. What comes out is what the clips put into
 * the track — gain, rides, fades, crossfades, stretches, transpositions and
 * reversals all baked in — and the project renders the same before and after,
 * which is the test.
 */

export interface ConsolidateRange {
  startTicks: number;
  endTicks: number;
  trackIds: string[];
}

export interface ConsolidateResult {
  project: Project;
  /** The clips that replaced the originals, one per rendered track. */
  clipIds: string[];
  trackIds: string[];
  durationSec: number;
}

/** Audio tracks in the range that have a clip inside it: the ones there is anything to render. */
export function consolidatableTracks(project: Project, range: ConsolidateRange): string[] {
  return range.trackIds.filter((trackId) => {
    const track = project.tracks[trackId];
    if (!track || track.kind !== 'audio') return false;
    return track.clipIds.some((clipId) => {
      const clip = project.clips[clipId];
      return (
        clip !== undefined &&
        clip.takeLane === 0 &&
        clip.startTicks < range.endTicks &&
        clipEndTicks(project, clip) > range.startTicks
      );
    });
  });
}

export async function consolidateRange(
  project: Project,
  assets: AssetManager,
  range: ConsolidateRange,
  sampleRate: number,
): Promise<ConsolidateResult | null> {
  const trackIds = consolidatableTracks(project, range);
  if (trackIds.length === 0 || range.endTicks <= range.startTicks) return null;

  const renders: { trackId: string; asset: AudioAssetRef; durationSec: number; name: string }[] = [];
  for (const trackId of trackIds) {
    const track = project.tracks[trackId];
    const buffer = await renderProject(project, assets, {
      startTicks: range.startTicks,
      endTicks: range.endTicks,
      sampleRate,
      // The length of the range exactly, or the clip would overlap what follows.
      tailSec: 0,
      isolateTrackId: trackId,
      clipsOnly: true,
      // The track's own width, so a mono track stays mono and the pan law does
      // not change underneath the clip.
      channelCount: trackChannelCount(project, trackId),
    });
    const wav = new Blob([encodeWav(bufferToChannels(buffer), buffer.sampleRate, { bitDepth: 24 }) as BlobPart], {
      type: 'audio/wav',
    });
    const name = `${track.name} (consolidated)`;
    const result = await assets.registerRecording(buffer, wav, name);
    renders.push({ trackId, asset: result.asset, durationSec: buffer.duration, name });
  }

  let next = project;
  for (const render of renders) {
    next = runCommand(next, { type: 'asset.register', params: { asset: render.asset } }).project;
  }
  // The range is cleared on the rendered tracks — clips inside it go, clips
  // straddling it are cut back to its edges — and one clip of the render takes
  // their place. Nothing else on the track moves.
  next = runCommand(next, {
    type: 'range.delete',
    params: { trackIds: renders.map((r) => r.trackId), startTicks: range.startTicks, endTicks: range.endTicks },
  }).project;
  const clipIds: string[] = [];
  for (const render of renders) {
    const clipId = createId('clp');
    clipIds.push(clipId);
    next = runCommand(next, {
      type: 'clip.addAudio',
      params: {
        clipId,
        trackId: render.trackId,
        assetId: render.asset.id,
        name: render.name,
        startTicks: range.startTicks,
        offsetSec: 0,
        durationSec: render.durationSec,
      },
    }).project;
  }

  return { project: next, clipIds, trackIds: renders.map((r) => r.trackId), durationSec: renders[0].durationSec };
}
